/**
 * Shared widget primitives for the Design board (Phase 1+).
 *
 * Phase 1 ships:
 *   - Structural primitives: renderSectionCard, renderControlGroup, renderToggleRow.
 *   - Hover-preview helper: wirePreviewOnHover + four snapshot factories
 *     (themeVarSnapshot, paletteSnapshot, pairingSnapshot, themePresetSnapshot).
 *   - Belt-and-suspenders revert listeners (lazy-installed once on the first
 *     wirePreviewOnHover call; idempotent).
 *
 * Subsequent phases fill the rest:
 *   - Phase 2 - 4: renderThemePresetCard, renderPalettePresetCard, renderFontPresetCard.
 *   - Phase 5: renderSegmented.
 *   - Phase 7: showChip / hideChip integration via design-preview-chip.js.
 *
 * Keystone design rule: hover-preview operations bypass the store entirely
 * (only `commit()` mutates state). Preview/revert use bridge.send directly,
 * so previews are invisible to the undo stack.
 */

import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { bridge } from '../editing/bridge.js';
import { getCurrentTheme, setThemeVar } from '../theme/theme-editor.js';
import {
  buildFontUpdatePayload,
  getCurrentFont,
  getCurrentHeadingFont,
  getCurrentHeadingFontWeight
} from '../theme/font-selector.js';
import {
  COLOR_PALETTES,
  applyPalette,
  applyFontPairing,
  applyThemePreset,
  paletteWouldOverwriteCustomColors,
  _buildButtonCSS
} from '../theme/theme-presets.js';
import { expandPaletteColors } from '../shared/theme-tokens.js';
import { FORM_SHAPES, FORM_STYLES, FORM_SUBMIT_STYLES } from '../shared/forms-presets-data.js';
import { showChip, hideChip } from './design-preview-chip.js';
import { showConfirmModal, showUndoToast } from './shell.js';
import { iconEl } from './icons.js';

// ─────────────────────────────────────────────────────────────────────────────
// Section card primitive
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a section card shell. Returns { wrap, body } so the caller can
 * fill the body with category-specific content.
 *
 * @param {Object} opts
 * @param {string} opts.id - Section id (e.g. 'colors'). Set as data-section.
 * @param {string} opts.title - Header title text.
 * @param {Function} [opts.onReset] - Optional Reset click handler.
 * @param {boolean} [opts.defaultCollapsed=false]
 * @returns {{ wrap: HTMLElement, body: HTMLElement }}
 */
export function renderSectionCard({ id, title, onReset, defaultCollapsed = false }) {
  const wrap = document.createElement('article');
  wrap.className = 'pb-design-section';
  wrap.dataset.section = id;
  if (defaultCollapsed) wrap.classList.add('pb-design-section--collapsed');

  const header = document.createElement('header');
  header.className = 'pb-design-section-header';

  const titleEl = document.createElement('h3');
  titleEl.className = 'pb-design-section-title';
  titleEl.id = `pb-design-section-title-${id}`;
  titleEl.textContent = title;
  header.appendChild(titleEl);

  if (typeof onReset === 'function') {
    const resetBtn = document.createElement('button');
    resetBtn.type = 'button';
    resetBtn.className = 'pb-design-section-reset';
    resetBtn.textContent = 'Reset';
    resetBtn.setAttribute('data-tooltip', `Reset ${title} to defaults`);
    resetBtn.setAttribute('aria-label', `Reset ${title} to defaults`);
    resetBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      onReset();
    });
    header.appendChild(resetBtn);
  }

  // Collapse chevron — toggles the section body (CSS controls visibility).
  const chevron = document.createElement('button');
  chevron.type = 'button';
  chevron.className = 'pb-design-section-chevron';
  chevron.setAttribute('aria-controls', `pb-design-section-body-${id}`);
  chevron.setAttribute('aria-expanded', defaultCollapsed ? 'false' : 'true');
  chevron.setAttribute('aria-label', `Toggle ${title}`);
  chevron.innerHTML =
    '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';
  chevron.addEventListener('click', (e) => {
    e.stopPropagation();
    const willCollapse = !wrap.classList.contains('pb-design-section--collapsed');
    wrap.classList.toggle('pb-design-section--collapsed', willCollapse);
    chevron.setAttribute('aria-expanded', willCollapse ? 'false' : 'true');
  });
  header.appendChild(chevron);

  const body = document.createElement('div');
  body.className = 'pb-design-section-body';
  body.id = `pb-design-section-body-${id}`;
  body.setAttribute('aria-labelledby', titleEl.id);

  wrap.appendChild(header);
  wrap.appendChild(body);

  return { wrap, body };
}

// ─────────────────────────────────────────────────────────────────────────────
// Control group primitive (label + control row)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Wrap a single control with a label. `controlHTML` may be either an
 * HTMLElement (appended directly) or a string (set via innerHTML on a slot).
 */
export function renderControlGroup(label, controlHTML) {
  const wrap = document.createElement('div');
  wrap.className = 'pb-design-control-group';

  const labelEl = document.createElement('label');
  labelEl.className = 'pb-design-control-label';
  labelEl.textContent = label;
  wrap.appendChild(labelEl);

  if (controlHTML instanceof HTMLElement) {
    wrap.appendChild(controlHTML);
  } else if (typeof controlHTML === 'string') {
    const slot = document.createElement('div');
    slot.className = 'pb-design-control-slot';
    slot.innerHTML = controlHTML;
    wrap.appendChild(slot);
  }

  return wrap;
}

// ─────────────────────────────────────────────────────────────────────────────
// Toggle row primitive (with optional 60×40 demo slot)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Toggle row: optional demo slot + label + description + switch.
 *
 * @param {Object} opts
 * @param {string} opts.key - State key (set as data-toggle-key).
 * @param {string} opts.label
 * @param {string} [opts.description]
 * @param {string|HTMLElement} [opts.demoHTML] - 60x40 demo slot content.
 * @param {boolean} opts.checked
 * @param {Function} [opts.onChange] - (newCheckedValue) => void
 */
export function renderToggleRow({ key, label, description, demoHTML, checked, onChange }) {
  const row = document.createElement('div');
  row.className = 'pb-design-toggle-row';
  row.dataset.toggleKey = key;

  if (demoHTML) {
    const demo = document.createElement('div');
    demo.className = 'pb-design-toggle-demo';
    demo.dataset.effect = key;
    if (demoHTML instanceof HTMLElement) demo.appendChild(demoHTML);
    else demo.innerHTML = demoHTML;
    row.appendChild(demo);
  }

  const textBlock = document.createElement('div');
  textBlock.className = 'pb-design-toggle-text';

  const labelEl = document.createElement('div');
  labelEl.className = 'pb-design-toggle-label';
  labelEl.textContent = label;
  textBlock.appendChild(labelEl);

  if (description) {
    const desc = document.createElement('div');
    desc.className = 'pb-design-toggle-description';
    desc.textContent = description;
    textBlock.appendChild(desc);
  }
  row.appendChild(textBlock);

  // Switch — reuses existing .pb-toggle-row + .pb-toggle-slider visual treatment.
  const switchLabel = document.createElement('label');
  switchLabel.className = 'pb-toggle-row pb-design-toggle-switch';
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.checked = !!checked;
  input.dataset.toggleKey = key;
  input.setAttribute('aria-label', label);
  if (typeof onChange === 'function') {
    input.addEventListener('change', () => onChange(input.checked));
  }
  const slider = document.createElement('span');
  slider.className = 'pb-toggle-slider';
  switchLabel.appendChild(input);
  switchLabel.appendChild(slider);
  row.appendChild(switchLabel);

  return row;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hover-preview helper + snapshot factories
// ─────────────────────────────────────────────────────────────────────────────
//
// Lifecycle:
//   1. mouseenter → setTimeout(delay).
//   2. Timer fires → captured = snapshot.capture(); snapshot.apply();
//      previewActive = true; the active snapshot is registered globally so
//      external events can revert it.
//   3. mouseleave → if timer pending, clear; if previewActive, revert + clear.
//   4. click → cancel timer, suppress next mouseleave revert, snapshot.commit()
//      (the only path that touches the store / undo stack).
//
// Belt-and-suspenders: a small set of global listeners (installed once,
// lazily, on first wirePreviewOnHover call) revert any active preview when:
//   - iframe:section-clicked or sidebar:tab-changed fires.
//   - mousedown happens outside any [data-preview-trigger] element.

let _activePreview = null; // { snapshot, captured }
let _revertListenersInstalled = false;
let _previewResyncPending = false;

function _resyncCommittedPreviewState() {
  const state = store.getState() || {};
  for (const [name, value] of Object.entries(state.theme || {})) {
    if (name.startsWith('--')) setThemeVar(name, value);
  }
  const bodyFont = state.font || 'Roboto';
  const headingFont = state.headingFont || bodyFont;
  bridge.send('update-font', buildFontUpdatePayload(bodyFont));
  bridge.send(
    'update-heading-font',
    buildFontUpdatePayload(headingFont, {
      weight: state.headingFontWeight || '700'
    })
  );
  bridge.send('update-button-styles', { css: _buildButtonCSS(state.buttonStyles || {}) });
}

function _registerActivePreview(snapshot, captured) {
  // Defensively revert any prior preview that didn't get cleaned up.
  if (_activePreview && _activePreview.snapshot !== snapshot) {
    _safeRevert(_activePreview.snapshot, _activePreview.captured);
  }
  _activePreview = { snapshot, captured };
}

function _clearActivePreview() {
  _activePreview = null;
}

function _safeRevert(snapshot, captured) {
  try {
    snapshot.revert(captured);
  } catch (err) {
    console.warn('[design-widgets] preview revert failed:', err);
  }
  // If a revert races an iframe teardown, the immediate postMessage can be
  // dropped. The next bridge-ready event replays CURRENT committed state, so
  // a stale hover value cannot survive a document swap.
  _previewResyncPending = true;
}

/** Revert any in-flight preview. Safe to call when nothing is active. */
export function revertActivePreviewIfAny() {
  if (!_activePreview) return;
  const { snapshot, captured } = _activePreview;
  _activePreview = null;
  _safeRevert(snapshot, captured);
  hideChip();
}

function _installRevertListenersOnce() {
  if (_revertListenersInstalled) return;
  _revertListenersInstalled = true;

  // Selecting a section in the iframe or switching sidebar tabs cancels any
  // in-flight preview (the trigger element may unmount before the cursor leaves).
  eventBus.on('iframe:section-clicked', revertActivePreviewIfAny);
  eventBus.on('sidebar:tab-changed', revertActivePreviewIfAny);
  eventBus.on('panel:tab-changed', revertActivePreviewIfAny);
  eventBus.on('iframe:bridge-ready', () => {
    if (!_previewResyncPending) return;
    _previewResyncPending = false;
    _resyncCommittedPreviewState();
  });

  // mousedown anywhere outside a preview trigger — covers the case where the
  // user clicks a tab, opens a modal, or clicks the canvas while a preview
  // is still in flight (no mouseleave guaranteed before the iframe re-renders).
  document.addEventListener(
    'mousedown',
    (e) => {
      if (!_activePreview) return;
      const target = e.target;
      if (target && target.closest && target.closest('[data-preview-trigger]')) return;
      revertActivePreviewIfAny();
    },
    true
  );
}

/**
 * Wire a preset card / button as a hover-preview trigger.
 *
 * @param {HTMLElement} triggerEl
 * @param {Object} snapshot - one of { themeVarSnapshot, paletteSnapshot,
 *   pairingSnapshot, themePresetSnapshot } return values, or any object
 *   exposing { capture(), apply(), revert(captured), commit() }.
 * @param {Object} [opts]
 * @param {number} [opts.delay=200]
 * @param {string} [opts.chipLabel] - Floating-chip text shown near the cursor
 *   while the preview is active (e.g. "Apply 'Aurora' palette"). Hidden on
 *   revert / commit / global revert.
 * @returns {Function} cleanup - removes listeners + reverts if active.
 */
export function wirePreviewOnHover(triggerEl, snapshot, { delay = 200, chipLabel = '' } = {}) {
  if (!triggerEl || !snapshot) return () => {};
  _installRevertListenersOnce();

  triggerEl.dataset.previewTrigger = '1';

  let timer = null;
  let previewActive = false;
  let captured = null;
  let suppressNextLeave = false;

  const startPreview = () => {
    timer = null;
    captured = snapshot.capture();
    try {
      snapshot.apply();
    } catch (err) {
      console.warn('[design-widgets] preview apply failed:', err);
      captured = null;
      return;
    }
    previewActive = true;
    _registerActivePreview(snapshot, captured);
    if (chipLabel) showChip(chipLabel);
  };

  const onEnter = () => {
    if (previewActive) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(startPreview, delay);
  };

  const onLeave = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (suppressNextLeave) {
      suppressNextLeave = false;
      hideChip();
      return;
    }
    if (previewActive) {
      previewActive = false;
      const cap = captured;
      captured = null;
      if (_activePreview && _activePreview.snapshot === snapshot) _clearActivePreview();
      _safeRevert(snapshot, cap);
      hideChip();
    }
  };

  const onClick = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    suppressNextLeave = previewActive; // post-click mouseleave should NOT revert
    previewActive = false;
    captured = null;
    if (_activePreview && _activePreview.snapshot === snapshot) _clearActivePreview();
    hideChip();
    try {
      const result = snapshot.commit();
      Promise.resolve(result)
        .then((committed) => {
          // A guarded action can resolve false when the user cancels.
          if (committed !== false && snapshot.undoLabel) {
            showUndoToast(snapshot.undoLabel, { id: 'undo-design-preset' });
          }
        })
        .catch((err) => {
          console.warn('[design-widgets] preview commit failed:', err);
        });
    } catch (err) {
      console.warn('[design-widgets] preview commit failed:', err);
    }
  };

  triggerEl.addEventListener('mouseenter', onEnter);
  triggerEl.addEventListener('mouseleave', onLeave);
  triggerEl.addEventListener('click', onClick);

  return function cleanup() {
    triggerEl.removeEventListener('mouseenter', onEnter);
    triggerEl.removeEventListener('mouseleave', onLeave);
    triggerEl.removeEventListener('click', onClick);
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (previewActive) {
      previewActive = false;
      const cap = captured;
      captured = null;
      if (_activePreview && _activePreview.snapshot === snapshot) _clearActivePreview();
      _safeRevert(snapshot, cap);
      hideChip();
    }
    delete triggerEl.dataset.previewTrigger;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Snapshot factories
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Snapshot for a single theme CSS variable. `commitFn` is provided by the
 * caller (e.g. () => updateThemeVar(name, value); commitThemeChange()) so
 * this factory stays pure and doesn't import the entire theme-editor.
 */
export function themeVarSnapshot(name, candidateValue, { commitFn } = {}) {
  return {
    capture() {
      const theme = getCurrentTheme() || {};
      return { name, value: theme[name] ?? '' };
    },
    apply() {
      setThemeVar(name, candidateValue);
    },
    revert(captured) {
      setThemeVar(captured.name, captured.value);
    },
    commit() {
      if (typeof commitFn === 'function') commitFn();
    }
  };
}

/** Snapshot for a full color palette (5+ CSS vars + applyPalette commit). */
export function paletteSnapshot(palette) {
  return {
    capture() {
      const theme = getCurrentTheme() || {};
      const colors = expandPaletteColors(palette?.colors || {}, { existingTheme: theme });
      const captured = {};
      for (const k of Object.keys(colors)) captured[k] = theme[k] ?? '';
      return captured;
    },
    apply() {
      const theme = getCurrentTheme() || {};
      const colors = expandPaletteColors(palette?.colors || {}, { existingTheme: theme });
      for (const [k, v] of Object.entries(colors)) {
        setThemeVar(k, v);
      }
    },
    revert(captured) {
      for (const [k, v] of Object.entries(captured || {})) {
        setThemeVar(k, v);
      }
    },
    async commit() {
      const currentTheme = getCurrentTheme() || {};
      let allowCustomOverwrite = false;
      if (paletteWouldOverwriteCustomColors(palette, currentTheme)) {
        allowCustomOverwrite = await showConfirmModal({
          title: 'Replace custom colors?',
          message: `Applying ${palette?.name || 'this palette'} will replace your individually customized theme colors. You can still undo this change.`,
          confirmLabel: 'Replace colors',
          cancelLabel: 'Cancel'
        });
        if (!allowCustomOverwrite) {
          _resyncCommittedPreviewState();
          return false;
        }
      }
      return applyPalette(palette, { allowCustomOverwrite });
    },
    undoLabel: 'Color palette applied'
  };
}

/** Snapshot for a font pairing (body + heading fonts + heading weight). */
export function pairingSnapshot(pairing) {
  return {
    capture() {
      return {
        font: getCurrentFont(),
        headingFont: getCurrentHeadingFont(),
        headingWeight: getCurrentHeadingFontWeight()
      };
    },
    apply() {
      if (pairing?.body) bridge.send('update-font', buildFontUpdatePayload(pairing.body));
      if (pairing?.heading) {
        bridge.send(
          'update-heading-font',
          buildFontUpdatePayload(pairing.heading, {
            weight: getCurrentHeadingFontWeight()
          })
        );
      }
    },
    revert(captured) {
      bridge.send('update-font', buildFontUpdatePayload(captured.font));
      bridge.send(
        'update-heading-font',
        buildFontUpdatePayload(captured.headingFont, {
          weight: captured.headingWeight
        })
      );
    },
    commit() {
      applyFontPairing(pairing);
    },
    undoLabel: 'Font pairing applied'
  };
}

/**
 * Snapshot for a full theme preset (palette + body font + heading font,
 * heading weight + button styles). Mirrors the bridge-send set in applyThemePreset
 * (theme-presets.js:227-268) exactly so revert restores every mutated field.
 */
export function themePresetSnapshot(preset) {
  const palette = COLOR_PALETTES.find((p) => p.id === preset?.paletteId);

  return {
    capture() {
      const theme = getCurrentTheme() || {};
      const colors = expandPaletteColors(palette?.colors || {}, { existingTheme: theme });
      const capturedColors = {};
      for (const k of Object.keys(colors)) capturedColors[k] = theme[k] ?? '';
      const state = store.getState() || {};
      return {
        colors: capturedColors,
        font: getCurrentFont(),
        headingFont: getCurrentHeadingFont(),
        headingWeight: getCurrentHeadingFontWeight(),
        buttonStyles: { ...(state.buttonStyles || {}) }
      };
    },
    apply() {
      const theme = getCurrentTheme() || {};
      const colors = expandPaletteColors(palette?.colors || {}, { existingTheme: theme });
      for (const [k, v] of Object.entries(colors)) {
        setThemeVar(k, v);
      }
      if (preset?.font) bridge.send('update-font', buildFontUpdatePayload(preset.font));
      if (preset?.headingFont) {
        bridge.send(
          'update-heading-font',
          buildFontUpdatePayload(preset.headingFont, {
            weight: getCurrentHeadingFontWeight()
          })
        );
      }
      if (preset?.buttonStyles) {
        const state = store.getState() || {};
        const merged = { ...(state.buttonStyles || {}), ...preset.buttonStyles };
        bridge.send('update-button-styles', { css: _buildButtonCSS(merged) });
      }
    },
    revert(captured) {
      for (const [k, v] of Object.entries(captured.colors)) {
        setThemeVar(k, v);
      }
      bridge.send('update-font', buildFontUpdatePayload(captured.font));
      bridge.send(
        'update-heading-font',
        buildFontUpdatePayload(captured.headingFont, {
          weight: captured.headingWeight
        })
      );
      bridge.send('update-button-styles', { css: _buildButtonCSS(captured.buttonStyles) });
    },
    commit() {
      applyThemePreset(preset);
    },
    undoLabel: 'Theme preset applied'
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Preset card primitives
// ─────────────────────────────────────────────────────────────────────────────

/** Caption and check share one selected-state owner: the card's aria-pressed. */
function appendPresetCaption(card, name) {
  const caption = document.createElement('span');
  caption.className = 'pb-preset-card__caption';
  const label = document.createElement('span');
  label.className = 'pb-preset-card__label';
  label.textContent = name;
  const check = document.createElement('span');
  check.className = 'pb-preset-card__check';
  check.setAttribute('aria-hidden', 'true');
  const glyph = iconEl('check');
  if (glyph) check.appendChild(glyph);
  caption.append(label, check);
  card.appendChild(caption);
}

/**
 * Render a theme preset card (Phase 2). Shows a heading-font "Aa" sample,
 * a tinted "Button" mini preview, and a stack of 3 palette circles.
 *
 * The caller is responsible for wiring hover-preview (via wirePreviewOnHover
 * + themePresetSnapshot) and click-to-commit. The card is a focusable
 * role="button" so keyboard users get parity (Enter/Space → click).
 *
 * @param {Object} preset - One of THEME_PRESETS entries (id, name, font,
 *   headingFont, paletteId, buttonStyles).
 * @param {Object} [opts]
 * @param {boolean} [opts.active=false] - Highlight as currently applied.
 * @returns {HTMLElement}
 */
export function renderThemePresetCard(preset, { active = false } = {}) {
  const palette = COLOR_PALETTES.find((p) => p.id === preset?.paletteId);
  const colors = palette?.colors || {};
  const primary = colors['--primary'] || '#333333';
  const radius = preset?.buttonStyles?.borderRadius || '0.25rem';
  const headingFont = preset?.headingFont || 'sans-serif';
  // Theme-card legacy classes are kept in parallel so existing CSS
  // (.pb-design-theme-card / --active border styling) applies until Phase 7
  // consolidates onto .pb-preset-card.
  const card = document.createElement('div');
  card.className = 'pb-preset-card pb-preset-card--theme pb-design-theme-card';
  if (active) {
    card.classList.add('pb-preset-card--active', 'pb-design-theme-card--active');
  }
  card.dataset.themeId = preset?.id || '';
  card.setAttribute('role', 'button');
  card.setAttribute('tabindex', '0');
  card.setAttribute('aria-label', `Apply ${preset?.name || 'theme preset'}`);
  card.setAttribute('aria-pressed', active ? 'true' : 'false');

  const fontSpan = document.createElement('span');
  fontSpan.className = 'pb-design-theme-font-preview';
  fontSpan.style.fontFamily = `'${headingFont}',sans-serif`;
  fontSpan.textContent = 'Aa';
  card.appendChild(fontSpan);

  const btnSpan = document.createElement('span');
  btnSpan.className = 'pb-design-theme-btn-preview';
  btnSpan.style.background = primary;
  btnSpan.style.color = '#fff';
  btnSpan.style.borderRadius = radius;
  btnSpan.textContent = 'Button';
  card.appendChild(btnSpan);

  const circles = document.createElement('span');
  circles.className = 'pb-design-circles';
  for (const key of ['--headerColor', '--primary', '--secondary']) {
    const dot = document.createElement('span');
    dot.className = 'pb-design-circle';
    dot.style.background = colors[key] || '#cccccc';
    circles.appendChild(dot);
  }
  card.appendChild(circles);
  appendPresetCaption(card, preset?.name || 'Theme preset');

  // Keyboard parity: Enter/Space triggers click so wirePreviewOnHover's
  // onClick handler runs (commits via snapshot.commit()).
  card.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      card.click();
    }
  });

  return card;
}

/**
 * Render a palette preset card (Phase 3). Shows a stack of 3 palette circles
 * representing the palette's header, primary, and secondary colors. The
 * caller wires hover-preview via wirePreviewOnHover + paletteSnapshot.
 *
 * @param {Object} palette - One of COLOR_PALETTES entries (id, name, colors).
 * @param {Object} [opts]
 * @param {boolean} [opts.active=false]
 * @returns {HTMLElement}
 */
export function renderPalettePresetCard(palette, { active = false } = {}) {
  const colors = palette?.colors || {};
  const card = document.createElement('div');
  card.className = 'pb-preset-card pb-preset-card--palette pb-design-palette-card';
  if (active) {
    card.classList.add('pb-preset-card--active', 'pb-design-palette-card--active');
  }
  card.dataset.paletteId = palette?.id || '';
  card.setAttribute('role', 'button');
  card.setAttribute('tabindex', '0');
  card.setAttribute('aria-label', `Apply ${palette?.name || 'palette'}`);
  card.setAttribute('aria-pressed', active ? 'true' : 'false');

  const circles = document.createElement('div');
  circles.className = 'pb-design-circles';
  for (const key of ['--headerColor', '--primary', '--secondary']) {
    const dot = document.createElement('span');
    dot.className = 'pb-design-circle';
    dot.style.background = colors[key] || '#cccccc';
    circles.appendChild(dot);
  }
  card.appendChild(circles);
  appendPresetCaption(card, palette?.name || 'Palette');

  card.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      card.click();
    }
  });

  return card;
}

/**
 * Render a font pairing card (Phase 4). Shows the heading font name in the
 * heading typeface and the body font name in the body typeface — same shape
 * as the legacy `.pb-design-pairing-card` so existing CSS still applies. The
 * caller wires hover-preview via `wirePreviewOnHover + pairingSnapshot`.
 *
 * @param {Object} pairing - One of FONT_PAIRINGS entries (heading, body).
 * @param {Object} [opts]
 * @param {boolean} [opts.active=false]
 * @returns {HTMLElement}
 */
export function renderFontPresetCard(pairing, { active = false } = {}) {
  const card = document.createElement('div');
  card.className = 'pb-preset-card pb-preset-card--font pb-design-pairing-card';
  if (active) {
    card.classList.add('pb-preset-card--active', 'pb-design-pairing-card--active');
  }
  card.dataset.heading = pairing?.heading || '';
  card.dataset.body = pairing?.body || '';
  card.setAttribute('role', 'button');
  card.setAttribute('tabindex', '0');
  card.setAttribute(
    'aria-label',
    `Apply font pairing: ${pairing?.heading || 'heading'} + ${pairing?.body || 'body'}`
  );
  card.setAttribute('aria-pressed', active ? 'true' : 'false');

  const headingEl = document.createElement('div');
  headingEl.className = 'pb-design-pairing-heading';
  headingEl.style.fontFamily = `'${pairing?.heading}',sans-serif`;
  headingEl.textContent = pairing?.heading || '';
  card.appendChild(headingEl);

  const bodyEl = document.createElement('div');
  bodyEl.className = 'pb-design-pairing-body';
  bodyEl.style.fontFamily = `'${pairing?.body}',sans-serif`;
  bodyEl.textContent = pairing?.body || '';
  card.appendChild(bodyEl);
  appendPresetCaption(card, `${pairing?.heading || 'Heading'} + ${pairing?.body || 'Body'}`);

  card.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      card.click();
    }
  });

  return card;
}

function _formPresetRules(kind, id) {
  if (kind === 'shape') return FORM_SHAPES.find((preset) => preset.id === id)?.fieldRules || {};
  if (kind === 'style') return FORM_STYLES.find((preset) => preset.id === id)?.fieldRules || {};
  if (kind === 'submit')
    return FORM_SUBMIT_STYLES.find((preset) => preset.id === id)?.submitRules || {};
  return {};
}

function _applyInlineRules(el, rules) {
  for (const [key, value] of Object.entries(rules || {})) {
    el.style.setProperty(key, value);
  }
}

export function renderFormPresetCard({ id, name, kind, active = false } = {}) {
  const card = document.createElement('div');
  card.className = 'pb-preset-card pb-preset-card--form pb-form-preset-card';
  if (active) card.classList.add('pb-preset-card--active');
  card.dataset.formPresetKind = kind || '';
  card.dataset.formPresetId = id || '';
  card.setAttribute('role', 'button');
  card.setAttribute('tabindex', '0');
  card.setAttribute('aria-label', `Apply form ${kind || 'preset'}: ${name || id || 'preset'}`);
  card.setAttribute('aria-pressed', active ? 'true' : 'false');

  const previewWrap = document.createElement('span');
  previewWrap.className = 'pb-form-preset-card__preview';
  const rules = _formPresetRules(kind, id);

  if (kind === 'submit') {
    const submit = document.createElement('span');
    submit.className = 'pb-form-preset-submit';
    submit.textContent = 'Submit';
    _applyInlineRules(submit, rules);
    previewWrap.appendChild(submit);
  } else {
    const input = document.createElement('input');
    input.className = 'pb-form-preset-preview';
    input.placeholder = 'Field';
    input.disabled = true;
    input.tabIndex = -1;
    _applyInlineRules(input, rules);
    previewWrap.appendChild(input);
  }

  card.appendChild(previewWrap);
  appendPresetCaption(card, name || id || 'Form preset');

  card.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      card.click();
    }
  });

  return card;
}

// ─────────────────────────────────────────────────────────────────────────────
// Segmented control primitive
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Render a pill-style segmented control. Options accept `{ name, label, value }`.
 * Clicking the active non-default option clears it, preserving the legacy
 * "tap active preset to return to default" behavior.
 */
export function renderSegmented({ field, options = [], value = '', onChange, ariaLabel } = {}) {
  const wrap = document.createElement('div');
  wrap.className = 'pb-segmented';
  wrap.dataset.field = field || '';
  wrap.setAttribute('role', 'radiogroup');
  if (ariaLabel) wrap.setAttribute('aria-label', ariaLabel);

  const buttons = [];
  const setValue = (nextValue) => {
    const normalized = nextValue || '';
    wrap.dataset.value = normalized;
    let checkedIndex = -1;
    buttons.forEach((btn, index) => {
      const active = btn.dataset.value === normalized;
      btn.classList.toggle('pb-active', active);
      btn.setAttribute('aria-checked', active ? 'true' : 'false');
      if (active) checkedIndex = index;
    });
    // ROVING TABINDEX (EA-4). This is a `radiogroup`, and a radiogroup is ONE
    // tab stop: Tab enters it, arrows move within it, Tab leaves it. Every
    // option being tabbable made a six-option control six tab stops, which is
    // what a keyboard-only pass would have found.
    const roving = checkedIndex === -1 ? 0 : checkedIndex;
    buttons.forEach((btn, index) => {
      btn.tabIndex = index === roving ? 0 : -1;
    });
  };

  for (const option of options) {
    const optionValue = option?.value || '';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pb-segmented-option';
    btn.dataset.field = field || '';
    btn.dataset.value = optionValue;
    btn.setAttribute('role', 'radio');
    btn.setAttribute('aria-checked', 'false');
    btn.textContent = option?.label || option?.name || optionValue || 'Default';

    btn.addEventListener('click', () => {
      const currentValue = wrap.dataset.value || '';
      const nextValue = currentValue === optionValue && optionValue !== '' ? '' : optionValue;
      setValue(nextValue);
      if (typeof onChange === 'function') onChange(nextValue, option);
    });

    btn.addEventListener('keydown', (e) => {
      if (
        e.key !== 'ArrowRight' &&
        e.key !== 'ArrowDown' &&
        e.key !== 'ArrowLeft' &&
        e.key !== 'ArrowUp'
      )
        return;
      e.preventDefault();
      const direction = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
      const index = buttons.indexOf(btn);
      const next = buttons[(index + direction + buttons.length) % buttons.length];
      if (!next) return;
      // Arrow keys SELECT, not merely focus. In a radiogroup, moving focus
      // without moving selection leaves a keyboard user looking at a highlight
      // that does not match the value — and there is no key that would then
      // commit it, because Space/Enter on a radio is a no-op once one is
      // checked. Pointer users get the same value from one click.
      next.focus();
      const nextValue = next.dataset.value || '';
      if ((wrap.dataset.value || '') === nextValue) return;
      setValue(nextValue);
      if (typeof onChange === 'function') {
        onChange(nextValue, options[buttons.indexOf(next)]);
      }
    });

    buttons.push(btn);
    wrap.appendChild(btn);
  }

  wrap.updateValue = setValue;
  setValue(value);
  return wrap;
}
